/* global CustomDatafeed */
import React, { useEffect, useRef, useState } from "react";
import { REACT_APP_API_BASE_URL } from "../../apiurl";
import "./TradingViewChart.css";

const TradingViewChart = ({ symbol, interval = "1D", token = null }) => {
  const containerRef = useRef(null);
  const widgetRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const loadScript = (src) => {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.async = false;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    };

    const loadTradingView = async () => {
      try {
        const baseUrl = REACT_APP_API_BASE_URL.endsWith("/") 
            ? REACT_APP_API_BASE_URL 
            : `${REACT_APP_API_BASE_URL}/`;

        await loadScript(`${baseUrl}api/charting_library-master/charting_library/charting_library.standalone.js`);
        await loadScript(`${baseUrl}api/charting_library-master/custom_datafeed.js`);

        setIsLoaded(true);
      } catch (err) {
        console.error("Failed to load TradingView scripts", err);
      }
    };

    loadTradingView();
  }, []);

  useEffect(() => {
    if (!isLoaded || !containerRef.current || !window.TradingView) return;
    
    // If symbol changes, we must flush/remove the old widget completely
    if (widgetRef.current) {
        try {
            widgetRef.current.remove();
            widgetRef.current = null;
        } catch (e) {
            console.warn("Error removing previous widget", e);
        }
    }

    const initWidget = () => {
        if (!window.CustomDatafeed && typeof CustomDatafeed === "undefined") {
             setTimeout(initWidget, 100);
             return;
        }

        const datafeed = typeof CustomDatafeed !== "undefined" ? CustomDatafeed : window.CustomDatafeed;
        const baseUrl = REACT_APP_API_BASE_URL.endsWith("/") ? REACT_APP_API_BASE_URL.slice(0, -1) : REACT_APP_API_BASE_URL;
        window.backendUrl = baseUrl;

        const widget = new window.TradingView.widget({
            container: containerRef.current,
            symbol: symbol,
            interval: interval,
            fullscreen: false,
            autosize: true,
            timezone: "Asia/Kolkata",
            locale: "en",
            theme: "light",
            datafeed: datafeed,
            library_path: `${baseUrl}/api/charting_library-master/charting_library/`,
            auto_save_delay: 1,

            disabled_features: [
                "use_localstorage_for_settings_save", 
                "header_compare", 
                "header_undo_redo",
                "hide_left_toolbar_by_default", 
                "header_fullscreen_button"
            ],
            enabled_features: [
                "header_saveload", 
                "header_widget", 
                "header_indicators", 
                "drawing_templates",
                "hide_left_toolbar_by_default"
            ],

            save_load_adapter: {
                getAllCharts: () => {
                    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
                    const url = `${baseUrl}/api/chart/list${token ? `?token=${token}` : ''}`;
                    return fetch(url, { headers })
                        .then(res => res.json())
                        .then(data => data.status === 'ok' ? data.data.map(item => ({
                            id: item.id.toString(),
                            name: item.name || `${item.symbol} Chart`,
                            symbol: item.symbol,
                            resolution: item.resolution,
                            timestamp: item.timestamp || Math.floor(Date.now() / 1000)
                        })) : []);
                },
                removeChart: (chartId) => {
                    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
                    return fetch(`${baseUrl}/api/chart/remove/${chartId}`, { method: 'DELETE', headers }).then(res => res.json());
                },
                saveChart: (chartData) => {
                    let exchange = "NSE";
                    if (chartData.symbol && chartData.symbol.startsWith("BSE:")) exchange = "BSE";
                    else if (chartData.symbol && chartData.symbol.startsWith("NSE:")) exchange = "NSE";

                    let sym = chartData.symbol ? chartData.symbol.replace(/^(NSE:|BSE:)/, "") : chartData.symbol;
                    if (sym && sym.includes("-")) sym = sym.split("-")[0];
                    
                    const payload = { 
                        id: chartData.id, 
                        name: chartData.name, 
                        symbol: sym, 
                        exchange: exchange,
                        resolution: chartData.resolution, 
                        content: chartData.content 
                    };
                    const headers = { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
                    const url = `${baseUrl}/api/chart/save${token ? `?token=${token}` : ''}`;
                    
                    return fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) })
                        .then(res => res.json())
                        .then(data => { if(data.status === 'ok') return data.id; throw new Error(data.message || 'Save failed'); })
                        .catch(err => { throw err; });
                },
                getChartContent: (chartId) => {
                    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
                    return fetch(`${baseUrl}/api/chart/list`, { headers })
                        .then(res => res.json())
                        .then(data => {
                            if(data.status === 'ok') {
                                const chart = data.data.find(c => c.id.toString() === chartId.toString());
                                if (chart && chart.content) return (typeof chart.content === 'object') ? JSON.stringify(chart.content) : chart.content;
                            }
                            return null;
                        });
                },
                getAllStudyTemplates: () => Promise.resolve([]),
                removeStudyTemplate: () => Promise.resolve(),
                saveStudyTemplate: () => Promise.resolve(),
                getStudyTemplateContent: () => Promise.resolve(null)
            },

            debug: false,
            market_status_button: true,
            marketIsOpen: true
        });

        widget.onChartReady(() => {
            // Auto-load matching chart
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
            fetch(`${baseUrl}/api/chart/list${token ? `?token=${token}` : ''}`, { headers })
                .then(res => res.json())
                .then(data => {
                    if (data.status === 'ok' && Array.isArray(data.data)) {
                        const cleanSymbol = symbol.replace(/^(NSE:|BSE:)/, "");
                        const matchedChart = data.data.find(c => c.symbol.replace(/^(NSE:|BSE:)/, "") === cleanSymbol);
                        if (matchedChart && matchedChart.content) {
                            try { widget.load(typeof matchedChart.content === 'string' ? JSON.parse(matchedChart.content) : matchedChart.content); } catch (e) { console.warn("Load failed", e); }
                        }
                    }
                })
                .catch(e => console.warn("Auto-load failed", e));
        });

        widgetRef.current = widget;
    };

    initWidget();

    return () => {
        if (widgetRef.current) {
            try {
                widgetRef.current.remove();
                widgetRef.current = null;
            } catch (e) {
                console.warn("Error removing widget", e);
            }
        }
    };
  }, [isLoaded, symbol, interval, token]);

  return (
    <div className="tradingview-chart-container">
      <div id="tv_chart_container" ref={containerRef} className="tradingview-chart-wrapper" />
    </div>
  );
};

export default TradingViewChart;
